export type BatchFacetChange = {
  operation: "add" | "remove";
  key: string;
  value: string;
};

export type BatchDraft = {
  collectionId: string;
  publication: "" | "published" | "hidden";
  archive: "" | "archived" | "active";
  saleStatus: "" | "unavailable" | "available" | "inquiry" | "sold";
  priceAction: "" | "set" | "clear";
  priceUsd: string;
  facets: BatchFacetChange[];
};

export type BatchChanges = {
  collectionId?: string;
  published?: boolean;
  archived?: boolean;
  saleStatus?: string;
  priceCents?: number | null;
  facets?: BatchFacetChange[];
};

export const emptyBatchDraft = (): BatchDraft => ({
  collectionId: "",
  publication: "",
  archive: "",
  saleStatus: "",
  priceAction: "",
  priceUsd: "",
  facets: [],
});

export function batchValidationError(draft: BatchDraft): string {
  if (draft.publication === "published" && draft.archive === "archived") {
    return "A work cannot be made publicly visible while it is being archived.";
  }
  if (draft.priceAction === "set") {
    const value = Number(draft.priceUsd);
    if (draft.priceUsd.trim() === "" || !Number.isFinite(value) || value < 0) {
      return "Enter a valid non-negative price, or choose Clear price.";
    }
  }
  return "";
}

export function buildBatchChanges(draft: BatchDraft): BatchChanges {
  const changes: BatchChanges = {};
  if (draft.collectionId) changes.collectionId = draft.collectionId;
  if (draft.publication) changes.published = draft.publication === "published";
  if (draft.archive) changes.archived = draft.archive === "archived";
  if (draft.saleStatus) changes.saleStatus = draft.saleStatus;
  if (draft.priceAction === "clear") changes.priceCents = null;
  if (draft.priceAction === "set" && !batchValidationError(draft)) {
    changes.priceCents = Math.round(Number(draft.priceUsd) * 100);
  }
  if (draft.facets.length) changes.facets = draft.facets;
  return changes;
}

export const hasBatchChanges = (changes: BatchChanges): boolean => Object.keys(changes).length > 0;

export function batchSummary(
  draft: BatchDraft,
  collectionLabel: (id: string) => string,
  facetLabel: (key: string) => string,
): string[] {
  const summary: string[] = [];
  if (draft.collectionId) summary.push(`Move to volume: ${collectionLabel(draft.collectionId)}`);
  if (draft.publication === "published") summary.push("Publish selected works");
  if (draft.publication === "hidden") summary.push("Hide selected works");
  if (draft.archive === "archived") summary.push("Archive selected works");
  if (draft.archive === "active") summary.push("Restore selected works from archive");
  if (draft.saleStatus) summary.push(`Sale status: ${draft.saleStatus}`);
  if (draft.priceAction === "clear") summary.push("Clear displayed price");
  if (draft.priceAction === "set" && !batchValidationError(draft)) summary.push(`Displayed price: $${Number(draft.priceUsd).toFixed(2)}`);
  for (const facet of draft.facets) {
    summary.push(`${facet.operation === "add" ? "Add" : "Remove"} ${facetLabel(facet.key)}: ${facet.value}`);
  }
  return summary;
}
